// Suggestion chips for the Higher Self conversation.
//
// Ported from iOS `HigherSelfChatView.suggestions` so the two apps offer the same openings: defaults
// on a fresh thread, then prompts themed to what the conversation is actually about.
//
// The scan is local and keyword-based on purpose. It runs on every render against text that is
// already in memory, and it must never become a reason to send the transcript anywhere — the chips
// are a convenience, not a feature worth spending privacy on.

export const DEFAULT_SUGGESTIONS = [
  'What am I avoiding?',
  'Show me a pattern',
  'Help me with a dream',
];

const THEMES: { keywords: string[]; chips: string[] }[] = [
  {
    keywords: ['tired', 'drained', 'exhaust', 'no energy', 'burned out', 'burnout', 'fatigue'],
    chips: ['Help me restore my energy', 'Why am I so drained?'],
  },
  {
    keywords: ['stress', 'overwhelm', 'pressure', 'anxious', 'anxiety', 'worry', 'panic'],
    chips: ['Help me lower my stress', "What's underneath this worry?"],
  },
  {
    keywords: ['sleep', 'insomnia', "can't sleep", 'wake up', 'waking', 'restless'],
    chips: ['Why is my sleep restless?', 'Help me wind down tonight'],
  },
  {
    keywords: ['dream', 'nightmare', 'dreamt', 'dreamed'],
    chips: ['What might this dream mean?', 'Find the symbol in this dream'],
  },
  {
    keywords: ['work', 'job', 'career', 'project', 'deadline', 'boss'],
    chips: ['What do I need at work right now?', 'Where is work pulling on me?'],
  },
  {
    keywords: ['relationship', 'partner', 'friend', 'family', 'mother', 'father', 'lonely'],
    chips: ["What's alive in this relationship?", 'What do I need from others?'],
  },
  {
    keywords: ['sad', 'grief', 'loss', 'cry', 'down', 'empty'],
    chips: ['Sit with this feeling with me', 'What is this sadness telling me?'],
  },
  {
    keywords: ['stuck', 'avoid', 'procrastinat', 'blocked', "can't start"],
    chips: ['What am I avoiding?', "What's one small next step?"],
  },
];

/**
 * Up to three chips for the current conversation.
 *
 * Only the last eight turns are read: a conversation that has moved on from work to sleep should
 * offer prompts about sleep, and an unbounded scan would keep suggesting whatever it opened with.
 */
export function chatSuggestions(recentMessages: { content: string }[]): string[] {
  const window = recentMessages.slice(-8);
  const haystack = window
    .map((m) => m.content)
    .join(' ')
    .toLowerCase();

  // A single opening turn is not yet a theme, so a fresh thread keeps the defaults.
  if (window.length < 2 || !haystack.trim()) return DEFAULT_SUGGESTIONS;

  const chips: string[] = [];
  for (const theme of THEMES) {
    if (!theme.keywords.some((word) => haystack.includes(word))) continue;
    chips.push(...theme.chips);
    if (chips.length >= 3) break;
  }
  if (chips.length === 0) return DEFAULT_SUGGESTIONS;

  // Top up from the defaults so the row never looks half-empty next to a themed pair.
  for (const fallback of DEFAULT_SUGGESTIONS) {
    if (chips.length >= 3) break;
    if (!chips.includes(fallback)) chips.push(fallback);
  }
  return chips.slice(0, 3);
}
